import React, { useEffect, useMemo, useState } from 'react';
import { DAYS_META } from '../data/questionsData';
import { quizService, getCurrentUserId } from '../services/quizService';
import { mapApiDayStatusToDayStatus } from '../services/assessmentApi';
import { ApiDayItem, DayStatus } from '../data/types';
import { QuizLoading } from './QuizLoading';
import { MentorAssessmentDashboard, isUserAdmin } from './MentorAssessmentDashboard';

interface QuizDashboardProps {
  studentName?: string;
  onSelectDay: (day: number) => void;
  apiDays?: ApiDayItem[];
  isLoading?: boolean;
}

const DAYPILL: Record<DayStatus, { label: string; fg: string; bg: string }> = {
  COMPLETED: { label: 'Đã hoàn thành', fg: '#0B6F79', bg: '#E1F3F4' },
  IN_PROGRESS: { label: 'Đang thực hiện', fg: '#8A6400', bg: '#FBF1D6' },
  AVAILABLE: { label: 'Chưa bắt đầu', fg: '#15243C', bg: '#E8ECF2' },
  LOCKED: { label: 'Đã khóa', fg: '#8A94A3', bg: '#EEF1F4' },
};

const pad = (n: number) => String(n).padStart(2, '0');
const fmtDT = (iso: string | null) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
};

export const QuizDashboard: React.FC<QuizDashboardProps> = ({
  studentName = 'Học viên',
  onSelectDay,
  apiDays,
  isLoading = false,
}) => {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const unsubscribe = quizService.subscribe(() => {
      setTick((t) => t + 1);
    });
    return unsubscribe;
  }, []);

  // Compute day cards from API or fallback to local session
  const daysData = useMemo(() => {
    if (Array.isArray(apiDays) && apiDays.length > 0) {
      return apiDays.map((apiItem) => {
        const dayNumber = Number(apiItem.day_number || apiItem.id);
        const meta = DAYS_META.find((m) => m.day === dayNumber);
        const session = quizService.getDaySession(dayNumber);

        // Total count from API or meta
        const count = apiItem.total_questions || meta?.totalQuestions || meta?.count || 34;

        // Status: prefer API if available
        let status: DayStatus = session.status;
        if (apiItem.status) {
          status = mapApiDayStatusToDayStatus(apiItem.status);
        }

        // Answered count: Ưu tiên lấy trực tiếp từ API (apiItem.progress: "X / Y")
        let answered: number | null = null;
        if (apiItem.progress) {
          const parts = apiItem.progress.split('/');
          const parsed = parseInt(parts[0].trim(), 10);
          if (!isNaN(parsed)) {
            answered = parsed;
          }
        }

        // Fallback: nếu không có dữ liệu API, chỉ tính số câu đã thực sự khóa/nộp (lockedQuestionIds) hoặc session.answers
        if (answered === null) {
          try {
            const uid = getCurrentUserId();
            const raw = localStorage.getItem(`fc_quiz_locked_u_${uid}_day_${dayNumber}`);
            if (raw) {
              const lockedIds = JSON.parse(raw);
              if (Array.isArray(lockedIds)) {
                answered = lockedIds.length;
              }
            }
          } catch {}

          if (answered === null) {
            answered = session.answers ? Object.keys(session.answers).length : 0;
          }
        }

        const locked = status === 'LOCKED';
        const pill = DAYPILL[status] || DAYPILL.AVAILABLE;
        const progressW =
          (count > 0 ? Math.min(100, Math.max(0, (answered / count) * 100)) : 0).toFixed(1) + '%';

        const dateLabel = session.submittedAt
          ? `Nộp ${fmtDT(session.submittedAt)}`
          : session.startedAt && status !== 'AVAILABLE'
          ? `Bắt đầu ${fmtDT(session.startedAt)}`
          : '';

        let actionLabel = 'Chưa mở';
        if (status === 'COMPLETED') {
          actionLabel = 'Xem lại câu trả lời';
        } else if (status === 'IN_PROGRESS') {
          actionLabel = `Tiếp tục Ngày ${dayNumber}`;
        } else if (status === 'AVAILABLE') {
          actionLabel = `Bắt đầu Ngày ${dayNumber}`;
        }

        const cleanTitle =
          apiItem.title?.replace(/^Ngày\s*\d+\s*[-–—:]\s*/i, '').trim() ||
          meta?.title ||
          `Ngày ${dayNumber}`;

        return {
          day: dayNumber,
          title: cleanTitle,
          count,
          answered,
          status,
          locked,
          pillLabel: pill.label,
          pillFg: pill.fg,
          pillBg: pill.bg,
          progressW,
          dateLabel,
          actionLabel,
        };
      });
    }

    // Fallback when apiDays is not yet loaded
    return DAYS_META.map((meta) => {
      const session = quizService.getDaySession(meta.day);
      const apiItem = apiDays?.find(
        (d) => Number(d.day_number || d.id) === Number(meta.day)
      );

      // Total count from API or meta
      const count = apiItem?.total_questions || meta.totalQuestions || meta.count || 34;

      // Status: prefer API if available
      let status: DayStatus = session.status;
      if (apiItem?.status) {
        status = mapApiDayStatusToDayStatus(apiItem.status);
      }

      // Answered count: Ưu tiên lấy trực tiếp từ API (apiItem.progress: "X / Y")
      let answered: number | null = null;
      if (apiItem && apiItem.progress) {
        const parts = apiItem.progress.split('/');
        const parsed = parseInt(parts[0].trim(), 10);
        if (!isNaN(parsed)) {
          answered = parsed;
        }
      }

      // Fallback: nếu không có dữ liệu API, chỉ tính số câu đã thực sự khóa/nộp (lockedQuestionIds) hoặc session.answers
      if (answered === null) {
        try {
          const uid = getCurrentUserId();
          const raw = localStorage.getItem(`fc_quiz_locked_u_${uid}_day_${meta.day}`);
          if (raw) {
            const lockedIds = JSON.parse(raw);
            if (Array.isArray(lockedIds)) {
              answered = lockedIds.length;
            }
          }
        } catch {}

        if (answered === null) {
          answered = session.answers ? Object.keys(session.answers).length : 0;
        }
      }

      const locked = status === 'LOCKED';
      const pill = DAYPILL[status] || DAYPILL.AVAILABLE;
      const progressW =
        (count > 0 ? Math.min(100, Math.max(0, (answered / count) * 100)) : 0).toFixed(1) + '%';

      const dateLabel = session.submittedAt
        ? `Nộp ${fmtDT(session.submittedAt)}`
        : session.startedAt && status !== 'AVAILABLE'
        ? `Bắt đầu ${fmtDT(session.startedAt)}`
        : '';

      let actionLabel = 'Chưa mở';
      if (status === 'COMPLETED') {
        actionLabel = 'Xem lại câu trả lời';
      } else if (status === 'IN_PROGRESS') {
        actionLabel = `Tiếp tục Ngày ${meta.day}`;
      } else if (status === 'AVAILABLE') {
        actionLabel = `Bắt đầu Ngày ${meta.day}`;
      }

      return {
        day: meta.day,
        title: apiItem?.title || meta.title,
        count,
        answered,
        status,
        locked,
        pillLabel: pill.label,
        pillFg: pill.fg,
        pillBg: pill.bg,
        progressW,
        dateLabel,
        actionLabel,
      };
    });
  }, [tick, apiDays]);

  const totalAnswered = useMemo(() => {
    return daysData.reduce((acc, d) => acc + d.answered, 0);
  }, [daysData]);

  const totalQuestions = useMemo(() => {
    return daysData.reduce((acc, d) => acc + d.count, 0) || 100;
  }, [daysData]);

  const totalProgressW = ((totalAnswered / totalQuestions) * 100).toFixed(1) + '%';

  // Overall status
  const overallStatus = useMemo(() => {
    if (totalAnswered === 0) {
      return DAYPILL.AVAILABLE;
    }
    const allCompleted = daysData.every((d) => d.status === 'COMPLETED');
    if (allCompleted) {
      return DAYPILL.COMPLETED;
    }
    return DAYPILL.IN_PROGRESS;
  }, [totalAnswered, daysData]);

  // Overall CTA
  const { ctaLabel, ctaTargetDay } = useMemo(() => {
    if (totalAnswered === 0) {
      return { ctaLabel: 'Bắt đầu bài đánh giá', ctaTargetDay: 1 };
    }
    const allCompleted = daysData.every((d) => d.status === 'COMPLETED');
    if (allCompleted) {
      return { ctaLabel: 'Xem lại Ngày 1', ctaTargetDay: 1 };
    }
    const activeDay = daysData.find((d) => d.status !== 'COMPLETED') || daysData[0];
    const ctaText =
      activeDay.status === 'IN_PROGRESS'
        ? `Tiếp tục Ngày ${activeDay.day}`
        : `Bắt đầu Ngày ${activeDay.day}`;
    return { ctaLabel: ctaText, ctaTargetDay: activeDay.day };
  }, [totalAnswered, daysData]);

  if (isLoading) {
    return <QuizLoading type="dashboard" message="Đang tải dữ liệu các ngày đánh giá..." />;
  }

  return (
    <div className="fc-dashboard-container">

      {/* Top Header */}
      <div className="fc-dash-header">
        <div>
          <div className="fc-dash-greeting">Xin chào, {studentName}</div>
          <h1 className="fc-dash-title">Đánh giá Năng lực</h1>
        </div>
        <span
          className="fc-dash-status-pill"
          style={{ background: overallStatus.bg, color: overallStatus.fg }}
        >
          {overallStatus.label}
        </span>
      </div>

      {/* Hero Progress Banner */}
      <div className="fc-dash-hero">
        <div className="fc-dash-hero-info">
          <div className="fc-dash-hero-label">TIẾN ĐỘ</div>
          <div className="fc-dash-hero-count">
            <span className="fc-dash-hero-num">{totalAnswered}</span>
            <span className="fc-dash-hero-total">/ {totalQuestions} câu</span>
          </div>
          <div className="fc-dash-hero-track">
            <div className="fc-dash-hero-fill" style={{ width: totalProgressW }} />
          </div>
        </div>
        <button
          type="button"
          className="fc-dash-cta-btn"
          onClick={() => onSelectDay(ctaTargetDay)}
        >
          {ctaLabel}
        </button>
      </div>

      {/* Days 3-Column Grid */}
      <div className="fc-dash-grid">
        {daysData.map((d) => (
          <div key={d.day} className="fc-dash-day-card">
            <div className="fc-dash-day-top">
              <div>
                <div className="fc-dash-day-name">Ngày {d.day}</div>
                <div className="fc-dash-day-sub">
                  {d.title} · {d.count} câu
                </div>
              </div>
              <span
                className="fc-dash-day-pill"
                style={{ background: d.pillBg, color: d.pillFg }}
              >
                {d.pillLabel}
              </span>
            </div>

            <div className="fc-dash-day-progress">
              <div className="fc-dash-day-track">
                <div className="fc-dash-day-fill" style={{ width: d.progressW }} />
              </div>
              <div className="fc-dash-day-meta">
                <span className="mono-font">
                  {d.answered} / {d.count}
                </span>
                <span>{d.dateLabel}</span>
              </div>
            </div>

            <button
              type="button"
              className="fc-dash-day-action-btn"
              onClick={() => onSelectDay(d.day)}
              disabled={d.locked}
            >
              {d.actionLabel}
            </button>
          </div>
        ))}
      </div>

      {/* Footer Note */}
      <div className="fc-dash-footer-note">
        Mỗi ngày là một phiên riêng. Câu trả lời được tự động lưu; sau khi hoàn thành một ngày, câu trả lời của ngày đó được khóa và ngày tiếp theo được mở.
      </div>

      {/* Mentor & Admin Assessment Dashboard (Chỉ hiện khi user có role admin hoặc super_admin) */}
      {isUserAdmin() && <MentorAssessmentDashboard apiDays={apiDays} />}
    </div>
  );
};

export default QuizDashboard;
